import { useEffect, useState } from "react"

interface CountdownProps {
  deadline?: number  // epoch ms
}

export function CountdownTimer({ deadline }: CountdownProps) {
  const [timeLeft, setTimeLeft] = useState<{ days: number; hours: number; minutes: number; seconds: number; expired: boolean }>({
    days: 0, hours: 0, minutes: 0, seconds: 0, expired: false,
  })

  useEffect(() => {
    if (!deadline) return
    const calc = () => {
      const now = Date.now()
      const diff = deadline - now
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true })
        return
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)
      setTimeLeft({ days, hours, minutes, seconds, expired: false })
    }
    calc()
    const interval = setInterval(calc, 1000)
    return () => clearInterval(interval)
  }, [deadline])

  if (!deadline) return null

  if (timeLeft.expired) {
    return (
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 text-sm font-semibold">
        <span className="w-2 h-2 rounded-full bg-red-500" />
        Application Deadline Passed
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="bg-white dark:bg-ink-800 rounded-lg p-3 text-center min-w-[60px] shadow-sm">
        <div className="font-display text-2xl md:text-3xl font-bold text-brand-700 dark:text-brand-300">{timeLeft.days}</div>
        <div className="text-[10px] uppercase tracking-wider text-ink-500 dark:text-ink-400">Days</div>
      </div>
      <div className="bg-white dark:bg-ink-800 rounded-lg p-3 text-center min-w-[60px] shadow-sm">
        <div className="font-display text-2xl md:text-3xl font-bold text-brand-700 dark:text-brand-300">{String(timeLeft.hours).padStart(2, "0")}</div>
        <div className="text-[10px] uppercase tracking-wider text-ink-500 dark:text-ink-400">Hrs</div>
      </div>
      <div className="bg-white dark:bg-ink-800 rounded-lg p-3 text-center min-w-[60px] shadow-sm">
        <div className="font-display text-2xl md:text-3xl font-bold text-brand-700 dark:text-brand-300">{String(timeLeft.minutes).padStart(2, "0")}</div>
        <div className="text-[10px] uppercase tracking-wider text-ink-500 dark:text-ink-400">Min</div>
      </div>
      <div className="bg-white dark:bg-ink-800 rounded-lg p-3 text-center min-w-[60px] shadow-sm">
        <div className="font-display text-2xl md:text-3xl font-bold text-gold-600 dark:text-gold-400">{String(timeLeft.seconds).padStart(2, "0")}</div>
        <div className="text-[10px] uppercase tracking-wider text-ink-500 dark:text-ink-400">Sec</div>
      </div>
    </div>
  )
}
